import { Icon } from '@client:common/icon';
import { AuthenticatedAccount } from '@client:common/AuthenticatedAccount';
import { GlobalStore } from '@client:common/global-store';
import useAssistantChat from 'ai-assistant/hooks/useAssistantChat';
import { Theme } from '@client:common/Theme';
import { PositionOverride } from '@client:common/global-store/GlobalState';

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import ReactGA from "react-ga4";

import './AssistantFrame.light.scss';
import './AssistantFrame.dark.scss';
import { AnimatePresence, motion, Transition } from 'framer-motion';
import { AssistantChatContent } from '../assistant-chat-content/AssistantChatContent';
import { useIsSmallScreen } from '@client:common/hooks/useIsSmallScreen';

const easingTransition: Transition = {
  type: 'spring',
  stiffness: 300,
  damping: 30
}

export interface AssistantFrameRef {
  close: () => void;
}

export const AssistantFrame = forwardRef<AssistantFrameRef>((_props, ref) => {
  const chat = useAssistantChat();
  const { isOpen, toggleAssistant } = chat;
  const isSmallScreen = useIsSmallScreen();

  const [authenticatedAccount, setAuthenticatedAccount] = useState<AuthenticatedAccount | null>(
    GlobalStore.getInstance().valueOf('authenticatedAccount')
  );

  const [currentTheme, setCurrentTheme] = useState<Theme>(
    GlobalStore.getInstance().valueOf('currentTheme') ?? Theme.LIGHT
  );

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showChatContent, setShowChatContent] = useState(false);
  const [positionOverride, setPositionOverride] = useState<PositionOverride | null>(null);

  useImperativeHandle(ref, () => ({
    close: () => {
      toggleAssistant(false);
    }
  }));

  useEffect(() => {
    const globalStore = GlobalStore.getInstance();
    const authSubscription = globalStore
      .select('authenticatedAccount')
      .subscribe(setAuthenticatedAccount);
    const themeSubscription = globalStore.select('currentTheme').subscribe(newTheme => {
      if (newTheme) {
        setCurrentTheme(newTheme);
      }
    });

    const positionSubscription = globalStore.select('assistantPositionOverride').subscribe(setPositionOverride);

    return () => {
      authSubscription.unsubscribe();
      themeSubscription.unsubscribe();
      positionSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (authenticatedAccount) {
      localStorage.removeItem('guestAssistantQuestions');
    }
  }, [authenticatedAccount]);

  useEffect(() => {
    if (!isOpen) {
      setShowChatContent(false);
      setIsFullScreen(false);
    } else {
      chat.coordinator.withNextRender(() => {
        setShowChatContent(true);
      })
    }
  }, [isOpen, chat.coordinator]);

  const handleOpenAIAssistant = () => {
    toggleAssistant(true);
    ReactGA.event({
      category: "AI Assistant",
      action: "opened_ai_assistant",
      label: "AI assistant chatbot opened"
    })
  }

  const handleCloseAIAssistant = () => {
    toggleAssistant(false);
    ReactGA.event({
      category: "AI Assistant",
      action: "closed_ai_assistant",
      label: "AI assistant chatbot closed"
    })
  }

  useEffect(() => {
    GlobalStore.getInstance().update({ isAssistantFullScreen: isFullScreen || (isSmallScreen && isOpen) });
  }, [isFullScreen, isSmallScreen, isOpen]);

  const shouldUseOverride = positionOverride && !isOpen && !isFullScreen && positionOverride.height !== '0px' && positionOverride.width !== '0px';
  return (
    <motion.div
      key={positionOverride ? isOpen ? 'override-open' : 'override-closed' : 'default'}
      className='assistant-frame-container'
      initial={{
        bottom: '2rem',
        right: '2rem',
        height: 'auto',
        width: 'auto'
      }}
      animate={{
        bottom: shouldUseOverride ? positionOverride.bottom : (isSmallScreen && (isOpen || isFullScreen) ? '0rem' : '2rem'),
        right: shouldUseOverride ? positionOverride.right : (isSmallScreen && (isOpen || isFullScreen) ? '0rem' : '2rem'),
        height: shouldUseOverride ? positionOverride.height : 'auto',
        width: shouldUseOverride ? positionOverride.width : 'auto',
      }}
      transition={positionOverride ? { duration: 0 } : easingTransition}
    >
      <motion.div
        className={`assistant-frame ${isFullScreen || (isSmallScreen && isOpen) ? 'assistant-frame--fullscreen' : ''}`}
        animate={{
          width: isFullScreen
            ? (isSmallScreen ? 'calc(100vw - 6px)' : 'calc(100vw - 4rem)')
            : isOpen
              ? (isSmallScreen ? 'calc(100vw - 6px)' : '800px')
              : 'auto',
          height: isFullScreen
            ? (isSmallScreen ? 'calc(100vh - 6px)' : 'calc(100vh - 4rem)')
            : isOpen
              ? (isSmallScreen ? '67vh' : '520px')
              : 'auto',
          maxWidth: isSmallScreen ? 'none' : 'calc(100vw - 4rem)',
          maxHeight: isSmallScreen ? 'none' : 'calc(100vh - 4rem)',
        }}
        transition={easingTransition}
      >
        <AnimatePresence>
          {isOpen ? (
            <motion.div
              className='assistant-frame__chat'
              initial={{ opacity: 0, scale: 0, transformOrigin: 'bottom right' }}
              animate={{ opacity: 1, scale: 1, transformOrigin: 'bottom right' }}
              exit={{ opacity: 0, scale: 0, transformOrigin: 'bottom right' }}
              transition={easingTransition}
            >
              <motion.div
                className='assistant-frame__header'
                initial={{ translateY: "-100%" }}
                animate={{ translateY: "0%" }}
                exit={{ translateY: "-100%" }}
                transition={{
                  type: 'spring',
                  stiffness: 200,
                  damping: 30,
                  delay: 0.15
                }}
              >
                <div className='assistant-frame__header-left'>
                  <img
                    className='banner__site-logo'
                    src={`/images/${currentTheme}-mode-site-logo.svg`}
                  />
                  <div className="assistant-frame__header-text">
                    <p className='assistant-frame__header-title'>AI Assistant</p>
                    <p className='assistant-frame__header-beta'>beta</p>
                  </div>
                </div>

                <div className='assistant-frame__header-right'>
                  <div className='assistant-frame__header-right icon' onClick={chat.handleReset}>
                    <Icon
                      icon='add'
                      size='32px'
                    />
                  </div>
                  <div className='assistant-frame__header-right icon' onClick={() => setIsFullScreen(!isFullScreen)}>
                    <Icon
                      icon={isFullScreen ? 'close_fullscreen' : 'open_in_full'}
                      size='28px'
                    />
                  </div>
                  <div className='assistant-frame__header-right icon' onClick={handleCloseAIAssistant}>
                    <Icon
                      icon='close'
                      size='32px'
                    />
                  </div>
                </div>
              </motion.div>

              {showChatContent && <AssistantChatContent chat={chat} />}
            </motion.div>
          ) : (
            <div className={`assistant-frame__button ${positionOverride ? 'assistant-frame__button--centered' : ''}`} onClick={handleOpenAIAssistant}>
              <Icon
                icon='chat'
                size='20px'
              />
              <h2 className='assistant-frame__title'>Ask AI</h2>
            </div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  )
});
